import React from 'react';
import { PlaceContext, DropPoint } from '@m4a3/api-client';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

interface RiskAssessment {
  groundRisk: 'HIGH' | 'LOW' | 'UNKNOWN' | 'ERROR';
  buildingRisk: 'HIGH' | 'LOW' | 'UNKNOWN' | 'ERROR';
  overallRisk: 'HIGH' | 'LOW' | 'UNKNOWN' | 'ERROR';
  canSave: boolean;
  message: string;
  heightData?: number;
  buildingReason?: string;
  hasError?: boolean;
}

export interface PlaceContextDropPointsProps {
  placeContext: PlaceContext | null;
  isLoading?: boolean;
  selectedDropPointId?: string | null;
  onDropPointSelect: (dropPointId: string, lat: number, lng: number) => void;
  shouldShowDropPoint?: (dropPoint: DropPoint) => boolean;
  className?: string;
  title?: string;
  subtitle?: string;
  isNarrow?: boolean;
  riskAssessment?: RiskAssessment | null;
  isLoadingRiskAssessment?: boolean;
  showManagementFeatures?: boolean;
  onDropPointDelete?: (dropPoint: DropPoint) => void;
  onSetPreferred?: (dropPoint: DropPoint) => void;
  onRemovePreferred?: () => void;
  showConfirmDelete?: string | null;
  onShowConfirmDelete?: (dropPointId: string | null) => void;
  showDeletedPoints?: boolean;
  onAddPin?: () => void;
  clickMode?: 'select' | 'add-drop-point';
  loading?: boolean;
  showDeletedPointsToggle?: boolean;
  onShowDeletedPointsChange?: (show: boolean) => void;
  settingPreferredId?: string | null;
  isRestrictedCell?: boolean;
}

const PlaceContextDropPoints = ({
  placeContext,
  isLoading = false,
  selectedDropPointId,
  onDropPointSelect,
  shouldShowDropPoint = () => true,
  className = '',
  title = 'Delivery Spots',
  subtitle = 'Select a delivery spot from this location',
  isNarrow = false,
  riskAssessment = null,
  isLoadingRiskAssessment = false,
  showManagementFeatures = false,
  onDropPointDelete,
  onSetPreferred,
  onRemovePreferred,
  showConfirmDelete,
  onShowConfirmDelete,
  showDeletedPoints = false,
  onAddPin,
  clickMode = 'select',
  loading = false,
  showDeletedPointsToggle = false,
  onShowDeletedPointsChange,
  settingPreferredId = null,
  isRestrictedCell = false,
}: PlaceContextDropPointsProps) => {
  const dropPoints = placeContext?.dropPoints?.data || [];
  const preferredDropPointId = placeContext?.place?.preferredDropPointId;
  // Partition into active and inactive groups, apply caller filter and sort by preferred then delivery count
  const activeDropPoints = dropPoints
    .filter(dp => shouldShowDropPoint(dp) && !dp.isDeleted)
    .sort((a, b) => {
      const aPreferred = a.id === preferredDropPointId ? 1 : 0;
      const bPreferred = b.id === preferredDropPointId ? 1 : 0;
      if (aPreferred !== bPreferred) return bPreferred - aPreferred;
      return (b.deliveryCount || 0) - (a.deliveryCount || 0);
    });
  const inactiveDropPoints = dropPoints
    .filter(dp => shouldShowDropPoint(dp) && dp.isDeleted)
    .sort((a, b) => {
      const aPreferred = a.id === preferredDropPointId ? 1 : 0;
      const bPreferred = b.id === preferredDropPointId ? 1 : 0;
      if (aPreferred !== bPreferred) return bPreferred - aPreferred;
      return (b.deliveryCount || 0) - (a.deliveryCount || 0);
    });

  const hasDropPoints = (activeDropPoints.length + inactiveDropPoints.length) > 0;
  const placeName = placeContext?.place?.friendlyName;
  const isPlaceEnabled = placeContext?.place?.enabled;

  if (isLoading && !settingPreferredId) {
    return (
      <div className={`w-full bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size={24} />
          <span className="ml-2 text-sm text-gray-600">Loading delivery spots...</span>
        </div>
      </div>
    );
  }

  if (!hasDropPoints && !isLoading) {
    return (
      <div className={`w-full bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
        {!isNarrow && (
          <div className="mb-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                {placeName && (
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm text-blue-600">📍 {placeName}</p>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        isPlaceEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {isPlaceEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                )}
                <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
              </div>
              <div className="flex items-center gap-2">
                {showManagementFeatures && onAddPin && (
                  <button
                    onClick={onAddPin}
                    disabled={loading || clickMode === 'add-drop-point'}
                    className="inline-flex items-center justify-center w-8 h-8 bg-black text-white rounded-full hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title={clickMode === 'add-drop-point' ? 'Adding Pin…' : 'Add Delivery Spot'}
                  >
                    {clickMode === 'add-drop-point' ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        <EmptyDropPointsState placeName={placeName} />
      </div>
    );
  }

  return (
    <div className={`w-full bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
      {!isNarrow && (
        <div className="mb-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              {placeName && (
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm text-blue-600">📍 {placeName}</p>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      isPlaceEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {isPlaceEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              )}
              <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
            </div>
            <div className="flex items-center gap-2">
              {showManagementFeatures && onAddPin && (
                <button
                  onClick={onAddPin}
                  disabled={loading || clickMode === 'add-drop-point'}
                  className="inline-flex items-center justify-center w-8 h-8 bg-black text-white rounded-full hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={clickMode === 'add-drop-point' ? 'Adding Pin…' : 'Add Delivery Spot'}
                >
                  {clickMode === 'add-drop-point' ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {/* Active section */}
        <div>
          <div className="mb-2 text-sm font-semibold text-gray-800">Active</div>
          <div className="space-y-2">
            {activeDropPoints.map((dropPoint: DropPoint) => (
              <DropPointRow
                key={dropPoint.id}
                dropPoint={dropPoint}
                placeContext={placeContext}
                isSelected={selectedDropPointId === dropPoint.id}
                onSelect={onDropPointSelect}
                riskAssessment={riskAssessment}
                isLoadingRiskAssessment={isLoadingRiskAssessment}
                selectedDropPointId={selectedDropPointId}
                isNarrow={isNarrow}
                showManagementFeatures={showManagementFeatures}
                onDropPointDelete={onDropPointDelete}
                onSetPreferred={onSetPreferred}
                onRemovePreferred={onRemovePreferred}
                showConfirmDelete={showConfirmDelete}
                onShowConfirmDelete={onShowConfirmDelete}
                showDeletedPoints={showDeletedPoints}
                settingPreferredId={settingPreferredId}
                isRestrictedCell={isRestrictedCell}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

interface DropPointRowProps {
  dropPoint: DropPoint;
  placeContext: PlaceContext | null;
  isSelected: boolean;
  onSelect: (dropPointId: string, lat: number, lng: number) => void;
  riskAssessment?: RiskAssessment | null;
  isLoadingRiskAssessment?: boolean;
  selectedDropPointId?: string | null;
  isNarrow?: boolean;
  showManagementFeatures?: boolean;
  onDropPointDelete?: (dropPoint: DropPoint) => void;
  onSetPreferred?: (dropPoint: DropPoint) => void;
  onRemovePreferred?: () => void;
  showConfirmDelete?: string | null;
  onShowConfirmDelete?: (dropPointId: string | null) => void;
  showDeletedPoints?: boolean;
  settingPreferredId?: string | null;
  isRestrictedCell?: boolean;
}

export const DropPointRow = ({
  dropPoint,
  placeContext,
  isSelected,
  onSelect,
  riskAssessment,
  isLoadingRiskAssessment,
  selectedDropPointId,
  isNarrow,
  showManagementFeatures,
  onDropPointDelete,
  onSetPreferred,
  onRemovePreferred,
  showConfirmDelete,
  onShowConfirmDelete,
  showDeletedPoints,
  settingPreferredId,
  isRestrictedCell = false,
}: DropPointRowProps) => {
  const deliveryCount = dropPoint.deliveryCount || 0;
  const riskLevel = dropPoint.risk === 0 ? 'LOW' : dropPoint.risk === 1 ? 'MED' : 'HIGH';
  const isPreferred = placeContext?.place?.preferredDropPointId === dropPoint.id;
  const isVerified = dropPoint.verified;
  const isDeleted = dropPoint.isDeleted;
  const isRestrictedUnverified = isRestrictedCell && !isVerified;

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'HIGH': return 'text-red-600';
      case 'MED': return 'text-orange-600';
      case 'LOW': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getRiskPillClasses = (risk: 'LOW' | 'MED' | 'HIGH' | 'UNKNOWN' | 'ERROR') => {
    // Outline style: white background, colored border + text
    switch (risk) {
      case 'LOW':
        return 'bg-white border border-green-300 text-green-700';
      case 'MED':
        return 'bg-white border border-orange-300 text-orange-700';
      case 'HIGH':
        return 'bg-white border border-red-300 text-red-700';
      case 'ERROR':
        return 'bg-white border border-orange-300 text-orange-700';
      default:
        return 'bg-white border border-gray-300 text-gray-700';
    }
  };

  const getVerificationPillClasses = (verified: boolean) => {
    return verified
      ? 'bg-white border border-green-300 text-green-700'
      : 'bg-white border border-red-300 text-red-700';
  };

  const titleCase = (risk: string) =>
    risk === 'MED' ? 'Mid' : risk.charAt(0) + risk.slice(1).toLowerCase();

  // Use actual createDate from dropPoint data
  const dateAdded = dropPoint.createDate ? new Date(dropPoint.createDate) : new Date();
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  };
  const formattedShortDate = (date: Date) => {
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
  };

  const deliveriesLabel = `${deliveryCount} ${deliveryCount === 1 ? 'delivery' : 'deliveries'}`;

  // Always show building/ground risk chips; when no assessment for this row, show placeholders

  return (
    <div
      className={`p-3 border rounded-md cursor-pointer transition-colors text-xs group ${
        isDeleted 
          ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed' 
          : isSelected 
            ? 'border-2 border-blue-500 bg-blue-50' 
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
      onClick={() => !isDeleted && onSelect(dropPoint.id, dropPoint.latitude, dropPoint.longitude)}
      title={`DP ${dropPoint.id.slice(-4)}`}
    >
      <div className="flex items-start space-x-3">
        {/* Checkbox */}
        <div className="flex-shrink-0 mt-0.5">
          <div className={`w-4 h-4 border-2 rounded-full flex items-center justify-center ${
            isSelected 
              ? 'border-blue-500 bg-blue-500' 
              : 'border-gray-300 bg-white'
          }`}>
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </div>

        {/* Content - Different layout for narrow vs full view */}
        <div className="flex-1 min-w-0">
          {isNarrow ? (
            /* Compact layout: concise but consistent with full view */
            <div className="text-xs">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{deliveriesLabel}</span>
                  <span className="text-gray-500">{formattedShortDate(dateAdded)}</span>
                </div>
                {isPreferred && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-600 text-white" title="Preferred">
                    ★ Preferred
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getRiskPillClasses((isSelected && selectedDropPointId === dropPoint.id && riskAssessment ? riskAssessment.buildingRisk : 'UNKNOWN') as any)}`}>
                  Building Risk: {isSelected && selectedDropPointId === dropPoint.id && riskAssessment 
                    ? `${titleCase(String(riskAssessment.buildingRisk))}${riskAssessment.buildingRisk === 'LOW' ? ' ✓' : ' ⚠️'}` 
                    : '—'}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getRiskPillClasses((isSelected && selectedDropPointId === dropPoint.id && riskAssessment ? riskAssessment.groundRisk : 'UNKNOWN') as any)}`}>
                  Ground Risk: {isSelected && selectedDropPointId === dropPoint.id && riskAssessment 
                    ? `${titleCase(String(riskAssessment.groundRisk))}${riskAssessment.groundRisk === 'LOW' ? ' ✓' : ' ⚠️'}` 
                    : '—'}
                </span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getVerificationPillClasses(isVerified)}`}
                  title={isVerified ? 'Verified' : 'Unverified'}
                >
                  {isVerified ? 'Verified' : 'Unverified'}
                </span>
              </div>
            </div>
          ) : (
            /* Full layout: card-style view */
            <>
              {/* Header row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-gray-800">
                  <div className="font-medium">{deliveriesLabel}</div>
                  <div className="text-gray-500">Active since {formatDate(dateAdded)}</div>
                </div>
                <div className="flex items-center gap-2"></div>
              </div>

              {/* Risk pills */}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getRiskPillClasses((isSelected && selectedDropPointId === dropPoint.id && riskAssessment ? riskAssessment.buildingRisk : 'UNKNOWN') as any)}`}>
                  Building Risk: {isSelected && selectedDropPointId === dropPoint.id && riskAssessment 
                    ? `${titleCase(String(riskAssessment.buildingRisk))}${riskAssessment.buildingRisk === 'LOW' ? ' ✓' : ' ⚠️'}` 
                    : '—'}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getRiskPillClasses((isSelected && selectedDropPointId === dropPoint.id && riskAssessment ? riskAssessment.groundRisk : 'UNKNOWN') as any)}`}>
                  Ground Risk: {isSelected && selectedDropPointId === dropPoint.id && riskAssessment 
                    ? `${titleCase(String(riskAssessment.groundRisk))}${riskAssessment.groundRisk === 'LOW' ? ' ✓' : ' ⚠️'}` 
                    : '—'}
                </span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getVerificationPillClasses(isVerified)}`}
                  title={isVerified ? 'Verified' : 'Unverified'}
                >
                  {isVerified ? 'Verified' : 'Unverified'}
                </span>
                {isPreferred && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-600 text-white" title="Preferred">
                    ★ Preferred
                  </span>
                )}
              </div>

              {/* Actions divider & row - show when selected and management features enabled */}
              {showManagementFeatures && isSelected && !isDeleted && (
                <>
                  <div className="my-2 border-t border-gray-200" />
                  <div className="flex items-center gap-2 text-xs">
                    {isPreferred ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemovePreferred?.();
                        }}
                        className="px-2 py-1 bg-white text-gray-800 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                        title="Remove preferred drop point"
                      >
                        Remove Preferred
                      </button>
                    ) : (
                      onSetPreferred && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onSetPreferred?.(dropPoint);
                          }}
                          disabled={settingPreferredId === dropPoint.id || isRestrictedUnverified}
                          className={`px-2 py-1 text-white rounded transition-colors ${
                            settingPreferredId === dropPoint.id || isRestrictedUnverified
                              ? 'bg-purple-400 cursor-not-allowed'
                              : 'bg-purple-600 hover:bg-purple-700'
                          }`}
                          title={isRestrictedUnverified ? 'Verify delivery spot before setting as preferred' : undefined}
                        >
                          {settingPreferredId === dropPoint.id ? 'Setting…' : 'Set as Preferred'}
                        </button>
                      )
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowConfirmDelete?.(dropPoint.id);
                      }}
                      className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      Deactivate
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      {showConfirmDelete === dropPoint.id && (
        <div className="mt-2 pt-2 border-t border-red-200 bg-red-50 p-2 rounded">
          <p className="text-xs text-red-800 mb-2">
            Are you sure you want to deactivate this delivery spot?
          </p>
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDropPointDelete?.(dropPoint);
              }}
              className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
            >
              Deactivate
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onShowConfirmDelete?.(null);
              }}
              className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface EmptyDropPointsStateProps {
  placeName?: string;
}

const EmptyDropPointsState = ({ placeName }: EmptyDropPointsStateProps) => {
  return (
    <div className="flex items-center justify-center py-8 px-8 text-gray-500">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-900 mb-1">No delivery spots available</p>
        <p className="text-xs text-gray-500">
          {placeName 
            ? `No delivery spots found for ${placeName}` 
            : 'No delivery spots found for this location'
          }
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Delivery spots will appear here once they are created for this address
        </p>
      </div>
    </div>
  );
};

export default PlaceContextDropPoints;
